import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { cn } from '../../shared/lib/cn';
import type { MeMembershipEntry, MeOrganizationOption, MeResponse } from '../../entities/me/me-contract.types';
import { fetchSuperadminOrganizations } from '../../features/superadmin/api/organizations.api';
import { staffImpersonationEnterOrganization } from '../../features/superadmin/model/impersonation-flow';
import { useAuth } from '../../shared/auth/use-auth';
import { resolveFirstAllowedRoute } from '../../shared/access/route-resolver';
import { canAccessPlatformOrganizationsList, isJwtPlatformSuperadminSession } from '../../shared/access/access';
import {
  isLegacyNumericOrgPlaceholder,
  orgNeutralLabel,
  orgNeutralTitle
} from '../../shared/lib/org-display-name';
import { useOrgDisplayName } from '../../shared/hooks/use-org-display-name';
import { canShowPlatformShellHome } from '../navigation/platform-shell-nav';
import styles from './staff-org-selector.module.css';

const EMPTY_CONTEXT_LABEL = 'Без контекста организации';
const NO_ORG_CONTEXT_LABEL = 'Нет контекста организации';
const SEARCH_PLACEHOLDER = 'Поиск…';
const PLATFORM_ORG_CATALOG_LABEL = 'Каталог организаций платформы';
const PLATFORM_ORG_LOOKUP_PAGE_LIMIT = 200;

type OrgOption = { id: number; name: string; itemTitle?: string };

type OrgOptionsModel = {
  /** Полный список для поиска (алфавит). */
  all: OrgOption[];
};

function labelFromMembership(m: MeMembershipEntry): OrgOption {
  const id = m.organization_id;
  const rawName = (m.organization_name ?? '').trim();
  if (rawName && !isLegacyNumericOrgPlaceholder(rawName)) {
    return { id, name: rawName };
  }
  const slug = (m.organization_slug ?? '').trim();
  if (slug) return { id, name: slug };
  return { id, name: orgNeutralLabel(id), itemTitle: orgNeutralTitle(id) };
}

function labelFromMeOrganization(o: MeOrganizationOption): OrgOption {
  const id = o.id;
  const rawName = (o.name ?? '').trim();
  if (rawName && !isLegacyNumericOrgPlaceholder(rawName)) {
    return { id, name: rawName };
  }
  const slug = (o.slug ?? '').trim();
  if (slug) return { id, name: slug };
  return { id, name: orgNeutralLabel(id), itemTitle: orgNeutralTitle(id) };
}

function labelFromSuperadminOrganization(o: { id: number; name?: string | null; slug?: string | null }): OrgOption {
  const id = o.id;
  const n = (o.name ?? '').trim();
  const s = (o.slug ?? '').trim();
  if (n && !isLegacyNumericOrgPlaceholder(n)) return { id, name: n };
  if (s) return { id, name: s };
  return { id, name: orgNeutralLabel(id), itemTitle: orgNeutralTitle(id) };
}

function buildOrgOptionsModel(me: MeResponse | null): OrgOptionsModel {
  if (!me) return { all: [] };
  const byId = new Map<number, OrgOption>();
  const seen = new Set<number>();

  for (const m of me.memberships ?? []) {
    if (seen.has(m.organization_id)) continue;
    seen.add(m.organization_id);
    byId.set(m.organization_id, labelFromMembership(m));
  }
  for (const o of me.organizations ?? []) {
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    byId.set(o.id, labelFromMeOrganization(o));
  }

  const all = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return { all };
}

function orgMatchesQuery(o: OrgOption, qNorm: string): boolean {
  if (!qNorm) return true;
  return o.name.toLowerCase().includes(qNorm) || String(o.id).includes(qNorm);
}

function isNeutralOrUnknownOrgDisplayName(o: OrgOption): boolean {
  const t = o.name.trim();
  if (isLegacyNumericOrgPlaceholder(t)) return true;
  if (t === 'Организация') return true;
  return t === orgNeutralLabel(o.id);
}

function pickRicherOrgLabel(a: OrgOption, b: OrgOption): OrgOption {
  const an = isNeutralOrUnknownOrgDisplayName(a);
  const bn = isNeutralOrUnknownOrgDisplayName(b);
  if (an && !bn) return b;
  if (!an && bn) return a;
  if (a.name.length !== b.name.length) return a.name.length >= b.name.length ? a : b;
  return a.id <= b.id ? a : b;
}

/** Каталог платформы + org из `/auth/me` (memberships); имя берём из более информативного источника. */
function mergeOrgOptionsFromMeAndPlatform(fromMe: OrgOptionsModel, platformItems: OrgOption[]): OrgOptionsModel {
  const byId = new Map<number, OrgOption>();
  for (const o of platformItems) {
    if (byId.has(o.id)) continue;
    byId.set(o.id, o);
  }
  for (const o of fromMe.all) {
    const existing = byId.get(o.id);
    if (existing) {
      byId.set(o.id, pickRicherOrgLabel(existing, o));
    } else {
      byId.set(o.id, o);
    }
  }
  const all = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  return { all };
}

/** Выбор организации (контекст JWT / имперсонация суперадмина). Паттерн как в первичном дизайне: лукап + поиск в панели. */
type PlatformOrgFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; items: OrgOption[] }
  | { status: 'error' };

async function fetchAllPlatformOrgOptions(
  apiClient: Parameters<typeof fetchSuperadminOrganizations>[0],
  signal: AbortSignal
): Promise<OrgOption[]> {
  const out: OrgOption[] = [];
  const seen = new Set<number>();
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  for (let page = 0; page < 100 && offset < total; page += 1) {
    const res = await fetchSuperadminOrganizations(apiClient, {
      limit: PLATFORM_ORG_LOOKUP_PAGE_LIMIT,
      offset,
      signal
    });
    total = res.total;
    if (res.items.length === 0) break;
    for (const item of res.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(labelFromSuperadminOrganization(item));
    }
    offset += res.items.length;
  }
  return out;
}

export type StaffOrgSelectorProps = {
  className?: string;
  /** Центр topbar в режиме платформы: в триггере — «Выбрать организацию», без дублирующей подписи слева. */
  platformTopbarPicker?: boolean;
};

export function StaffOrgSelector({ className, platformTopbarPicker }: StaffOrgSelectorProps) {
  const { me, impersonateOrgId, setImpersonateOrgId, refreshMe, apiClient } = useAuth();
  const navigate = useNavigate();
  const fromMe = useMemo(() => buildOrgOptionsModel(me), [me]);
  const [platformOrgs, setPlatformOrgs] = useState<PlatformOrgFetchState>({ status: 'idle' });
  /** Защита от гонки: старый fetch не перезаписывает состояние после abort/нового запроса. */
  const platformOrgFetchGen = useRef(0);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const effectiveOrgId = impersonateOrgId ?? me?.organization_id ?? null;
  const canPickNoContext = canShowPlatformShellHome(me?.capabilities);
  const jwtPlatformSuperadmin = isJwtPlatformSuperadminSession(me);
  const showClearImpersonation =
    effectiveOrgId != null && effectiveOrgId > 0 && jwtPlatformSuperadmin;

  const platformShell = me != null && canShowPlatformShellHome(me.capabilities);
  const canListPlatformOrgs = jwtPlatformSuperadmin || canAccessPlatformOrganizationsList(me?.capabilities);

  useEffect(() => {
    if (!me) return;
    if (!platformShell) {
      setPlatformOrgs({ status: 'idle' });
      return;
    }
    if (!canListPlatformOrgs) {
      setPlatformOrgs({ status: 'ready', items: [] });
      return;
    }

    const mine = ++platformOrgFetchGen.current;
    setPlatformOrgs({ status: 'loading' });
    const ac = new AbortController();
    void fetchAllPlatformOrgOptions(apiClient, ac.signal)
      .then((items) => {
        if (mine !== platformOrgFetchGen.current) return;
        setPlatformOrgs({ status: 'ready', items });
      })
      .catch(() => {
        if (ac.signal.aborted || mine !== platformOrgFetchGen.current) return;
        setPlatformOrgs({ status: 'error' });
      });
    return () => {
      ac.abort();
      platformOrgFetchGen.current += 1;
    };
  }, [
    me,
    impersonateOrgId,
    apiClient,
    platformShell,
    canListPlatformOrgs
  ]);

  const merged = useMemo((): OrgOptionsModel => {
    if (platformOrgs.status === 'ready' && platformOrgs.items.length > 0) {
      return mergeOrgOptionsFromMeAndPlatform(fromMe, platformOrgs.items);
    }
    return fromMe;
  }, [fromMe, platformOrgs]);

  const { all: allOrgs } = merged;

  const platformListLoading =
    platformShell &&
    canListPlatformOrgs &&
    (platformOrgs.status === 'loading' || platformOrgs.status === 'idle');

  const showPlatformCatalogLink =
    platformShell &&
    allOrgs.length === 0 &&
    !platformListLoading &&
    (!canListPlatformOrgs ||
      platformOrgs.status === 'error' ||
      (platformOrgs.status === 'ready' && platformOrgs.items.length === 0));

  const searchNorm = search.trim().toLowerCase();
  const displayRows = useMemo(() => {
    if (searchNorm.length === 0) return allOrgs;
    return allOrgs.filter((o) => orgMatchesQuery(o, searchNorm));
  }, [allOrgs, searchNorm]);

  const showCatalogRow =
    showPlatformCatalogLink &&
    (!searchNorm || PLATFORM_ORG_CATALOG_LABEL.toLowerCase().includes(searchNorm));
  const showEmptyContextRow =
    canPickNoContext && (!searchNorm || EMPTY_CONTEXT_LABEL.toLowerCase().includes(searchNorm));
  const showNoOrgResults =
    searchNorm.length > 0 && displayRows.length === 0 && !showCatalogRow && !showEmptyContextRow;

  const activeHit = useMemo(() => {
    if (effectiveOrgId == null || effectiveOrgId <= 0) return null;
    return allOrgs.find((o) => o.id === effectiveOrgId) ?? null;
  }, [allOrgs, effectiveOrgId]);

  const preferActiveName = useMemo(() => {
    if (!activeHit) return null;
    const n = (activeHit.name ?? '').trim();
    if (!n || isLegacyNumericOrgPlaceholder(n)) return null;
    return n;
  }, [activeHit]);

  const {
    displayName: resolvedTriggerLabel,
    title: resolvedTriggerTitle
  } = useOrgDisplayName(effectiveOrgId, { preferName: preferActiveName });

  const displayText = useMemo(() => {
    if (effectiveOrgId != null && effectiveOrgId > 0) return `Организация: ${resolvedTriggerLabel}`;
    if (canPickNoContext && platformTopbarPicker) return 'Выбрать организацию';
    if (canPickNoContext) return EMPTY_CONTEXT_LABEL;
    return NO_ORG_CONTEXT_LABEL;
  }, [effectiveOrgId, resolvedTriggerLabel, canPickNoContext, platformTopbarPicker]);

  const displayMuted = !(effectiveOrgId != null && effectiveOrgId > 0) && !canPickNoContext;

  async function clearImpersonation(): Promise<void> {
    await setImpersonateOrgId(null);
    const m = await refreshMe(
      isJwtPlatformSuperadminSession(me) ? { superadminClearEffectiveOrg: true } : undefined
    );
    navigate(resolveFirstAllowedRoute(m?.capabilities ?? null, m), { replace: true });
  }

  if (!me) return null;

  if (fromMe.all.length === 0 && !platformShell) {
    return (
      <p className={cn('truncate text-sm text-[hsl(var(--ui-muted-foreground))]', className)}>
        Нет доступных организаций
      </p>
    );
  }

  return (
    <div className={cn(styles.wrap, className)}>
      <Popover.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setSearch('');
            return;
          }
          // Повтор при открытии: снимает редкую гонку (abort + поздний ответ) и «пустой» первый проход.
          if (
            platformShell &&
            canListPlatformOrgs &&
            platformOrgs.status === 'ready' &&
            platformOrgs.items.length === 0
          ) {
            const mine = ++platformOrgFetchGen.current;
            setPlatformOrgs({ status: 'loading' });
            const ac = new AbortController();
            void fetchAllPlatformOrgOptions(apiClient, ac.signal)
              .then((items) => {
                if (mine !== platformOrgFetchGen.current) return;
                setPlatformOrgs({ status: 'ready', items });
              })
              .catch(() => {
                if (ac.signal.aborted || mine !== platformOrgFetchGen.current) return;
                setPlatformOrgs({ status: 'error' });
              });
          }
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(styles.trigger, showClearImpersonation && styles.triggerWithClear)}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label="Выбор организации"
            title={resolvedTriggerTitle ?? (activeHit?.itemTitle ?? undefined)}
          >
            <span
              className={cn('min-w-0 flex-1 truncate', displayMuted && styles.triggerMuted)}
            >
              {displayText}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
          </button>
        </Popover.Trigger>
        {showClearImpersonation ? (
          <button
            type="button"
            className={styles.clearBtn}
            aria-label="Сбросить выбор организации"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              void clearImpersonation();
            }}
          >
            <X className="size-4" aria-hidden />
          </button>
        ) : null}
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={12}
            className={styles.popoverPanel}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              queueMicrotask(() => searchInputRef.current?.focus());
            }}
          >
            <Command shouldFilter={false}>
              <div className={styles.searchBar}>
                <Command.Input
                  ref={searchInputRef}
                  placeholder={SEARCH_PLACEHOLDER}
                  className={styles.searchInput}
                  value={search}
                  onValueChange={setSearch}
                />
              </div>
              <Command.List className={styles.list} onWheelCapture={(e) => e.stopPropagation()}>
                {platformListLoading ? (
                  <div className={styles.emptyHint} role="status">
                    Загружаем список организаций…
                  </div>
                ) : null}
                {showCatalogRow ? (
                  <Command.Item
                    value={`__catalog__ ${PLATFORM_ORG_CATALOG_LABEL} каталог платформы привязанных организаций`}
                    onSelect={() => {
                      setOpen(false);
                      navigate(ROUTE_PATHS.superadminOrganizations);
                    }}
                    className={cn(
                      styles.item,
                      'data-[selected=true]:bg-[hsl(var(--ui-muted))] aria-selected:bg-[hsl(var(--ui-muted))]'
                    )}
                  >
                    <span className="flex min-w-0 flex-col gap-0.5 text-left">
                      <span className="truncate font-medium">{PLATFORM_ORG_CATALOG_LABEL}</span>
                      <span className="text-xs font-normal text-[hsl(var(--ui-muted-foreground))]">
                        {!canListPlatformOrgs
                          ? 'Нет доступа к списку платформы в текущей сессии — откройте раздел через меню, если он у вас есть.'
                          : platformOrgs.status === 'error'
                            ? 'Не удалось подгрузить список для быстрого выбора. Откройте страницу каталога.'
                            : 'В каталоге платформы пока нет организаций.'}
                      </span>
                    </span>
                  </Command.Item>
                ) : null}
                {showEmptyContextRow ? (
                  <Command.Item
                    value={`__empty__ ${EMPTY_CONTEXT_LABEL}`}
                    onSelect={() => {
                      setOpen(false);
                      void clearImpersonation();
                    }}
                    className={cn(
                      styles.item,
                      'data-[selected=true]:bg-[hsl(var(--ui-muted))] aria-selected:bg-[hsl(var(--ui-muted))]'
                    )}
                  >
                    <Check
                      className={cn(
                        'size-4 shrink-0 text-[hsl(var(--ui-primary))]',
                        effectiveOrgId == null || effectiveOrgId <= 0 ? 'opacity-100' : 'opacity-0'
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">{EMPTY_CONTEXT_LABEL}</span>
                  </Command.Item>
                ) : null}
                {displayRows.map((o) => (
                  <Command.Item
                    key={o.id}
                    value={`${o.id} ${o.name}`}
                    onSelect={() => {
                      setOpen(false);
                      void staffImpersonationEnterOrganization({
                        organizationId: o.id,
                        setImpersonateOrgId,
                        refreshMe,
                        navigate: (to, opts) => navigate(to, opts)
                      });
                    }}
                    className={cn(
                      styles.item,
                      'data-[selected=true]:bg-[hsl(var(--ui-muted))] aria-selected:bg-[hsl(var(--ui-muted))]'
                    )}
                  >
                    <Check
                      className={cn(
                        'size-4 shrink-0 text-[hsl(var(--ui-primary))]',
                        effectiveOrgId === o.id ? 'opacity-100' : 'opacity-0'
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate" title={o.itemTitle ?? o.name}>
                      {o.name}
                    </span>
                  </Command.Item>
                ))}
                {showNoOrgResults ? (
                  <div className={styles.emptyHint} role="status">
                    Ничего не найдено
                  </div>
                ) : null}
              </Command.List>
            </Command>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
